import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs } from "../../../Styles";
import { NewFieldStyles, NewFieldTypes, ValueType } from "../../../Definitions";
import { useFeedcurveOptions, useSectionOptions } from "../../_Components";
import { useAppSelector } from "../../../Store";
import { selectAllFeedCurveMilestoneOptions } from "../../../Store/OptionsHandlers/optionsCombinedReducer";
import ProgressBar from "../../ProgressViewer/progressBar";
import { NewInput, NewSelect } from "../../BaseComponents";
import FeedCorrectionPopover from "./feedCorrectionPopover";
import { currentViewTypes, usePenContext } from "../_Animals/locationContext";
import { useFeedCurveById } from "../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/FeedDataSlices/feedCurvesSlice";
import { Animal } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/getAnimalCall";
import { useAnimalById } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/AnimalDataSlices/animalsSlice";
import { useAnimalConsumptionById } from "../../../Services/Agrisys_GqlDataCaller/ConsumptionCalls/consumptionSlice";
import { useSingleConsumptionByMultipleIds } from "../_Animals/singleConsumptionByMultiple";
import { useSingleConsumption } from "../../_Components/consumption";

/* eslint-disable */
enum ViewTypes {
  advanced = "advanced",
  simple = "simple"
}
/* eslint-enable */

const ANIMAL = ({ penIndex, animalIndex, defaultCursor }: { penIndex: number, animalIndex: number, defaultCursor: boolean }) => {
  const { t } = useTranslation();
  const { feedCurveOptions } = useFeedcurveOptions();
  const { locationOptions } = useSectionOptions();

  const { state: { pens, tempData, currentViewType }, dispatch } = usePenContext()
  // #region | Animal
  const { entity: animal } = useAnimalById(animalIndex)

  const thisAnimal = useMemo(() => {
    let AnimalData: Animal | undefined = animal
    if (tempData && tempData.length) {
      let changes = tempData.find(e => (e.penId === penIndex || e.data.location === penIndex) && e.animalId === animalIndex)
      if (changes?.data.fk_feedcurve_milestones && AnimalData) {
        AnimalData = { ...AnimalData, feedcurve_milestones: changes.data.fk_feedcurve_milestones };
      }
      if (changes) {
        AnimalData = { ...AnimalData, ...changes.data } as Animal;
      }
    }
    return AnimalData
  }, [penIndex, animalIndex, tempData, animal])

  const { isLoading, entity: feedCurve } = useFeedCurveById(animal?.feedcurve_id)

  const animalConsumption = useSingleConsumption(animal?.animal_id ?? 0)

  const animalInfo = useMemo(() => {
    // Feed Correction
    const feedCorrection = animal
      ? animal.correction === undefined || animal.correction === null
        ? ""
        : animal.correction === 0
          ? "0%"
          : `${animal.correction}%`
      : "0%";

    // Milestone Day
    const milestoneDayNo = animal?.milestone_day
      ? Math.floor(
        (new Date().getTime() - animal.milestone_day) / (1000 * 60 * 60 * 24)
      )
      : undefined;

    // Milestone Name
    const milestoneName = animal?.milestone ? animal.milestone.name : "";
    const milestoneId = animal?.milestone ? animal.milestone.milestone_id : "";

    const feedCurveName = feedCurve?.name //= feedCurvesData.find(f => f.feedCurveId === ThisAnimal?.feedCurve?.feedCurveId)?.name

    return {
      feedCurveName,
      feedCorrection,
      milestone: { dayNo: milestoneDayNo, name: milestoneName, id: milestoneId },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedCurve, animal])
  // #endregion

  // #region | Progressbar
  const ConsumptionProgressBar: React.FC<{ ifHasExtra?: boolean }> = ({ ifHasExtra }) => {
    const const_Today = animalConsumption[0].feed

    if (!const_Today) return <></>;
    return (
      <ProgressBar
        extension={{ extensionOnly: ifHasExtra }}
        total={+(const_Today.total ?? 0)}
        progressed={+(const_Today.remaning ?? 0)}
      />
    );
  };

  const ProgressWithExtra = useMemo(() => (
    <ConsumptionProgressBar ifHasExtra={true} />
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [animal, animalConsumption]);

  const ProgressWithoutExtra = useMemo(() => (
    <ConsumptionProgressBar ifHasExtra={false} />
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [animal, animalConsumption]);
  // #endregion

  const textStyle = { ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, cursor: defaultCursor ? "default" : "pointer" }

  const feedCurveMilestones = useAppSelector(selectAllFeedCurveMilestoneOptions).reduce((agg, milestone) => {
    const { feedCurveId } = milestone;
    agg[feedCurveId] = agg[feedCurveId] || []
    agg[feedCurveId].push(milestone)
    return agg;
  }, {} as { [key: number]: (ValueType & { feedCurveId: number; index: number; milestoneId: number; })[] });
  const milestoneOptions = feedCurveMilestones[Number(thisAnimal?.feedcurve_id)] ?? []

  if (animal === undefined) return <>DATA ERROR</>
  switch (currentViewType) {
    case currentViewTypes.basic:
      return (
        <div style={{ display: "grid", gridTemplateColumns: "45px 108px 45px 108px 45px 90px 50px", height: "35px", textAlign: "start", alignItems: "center", gap: "5px", cursor: "pointer" }}>
          <div style={{ ...textStyle }}>{animal.animal_number}</div>
          <div style={{ ...textStyle }}>{animalInfo.milestone.name}</div>
          <div style={{ ...textStyle }}>{animalInfo.milestone.dayNo}</div>
          <div style={{ ...textStyle, paddingLeft: "5px" }}>{animalInfo.feedCurveName}</div>
          <div style={{ ...textStyle, paddingLeft: "5px" }}>{animalInfo.feedCorrection}</div>
          <div style={{ ...textStyle }}>{ProgressWithoutExtra}</div>
          <div style={{ ...textStyle }}>{ProgressWithExtra}</div>
        </div>
      )
    case currentViewTypes.edit:
      return (
        <div style={{ display: "grid", gridTemplateColumns: "45px 108px 45px 108px 45px 90px 50px", height: "35px", textAlign: "start", alignItems: "center", gap: "5px" }}>
          <div>{animal.animal_number}</div>
          <NewSelect
            onChange={(selectedValue) => dispatch({ tempData: [{ penId: penIndex, animalId: animalIndex, data: { fk_feedcurve_milestones: Number(selectedValue?.id) } }] })}
            options={milestoneOptions}
            fieldOverrides={{
              fieldStyle: NewFieldStyles.greyTertiary,
              fieldType: NewFieldTypes.tertiaryField,
              fieldWidth: "100%",
              menuWidth: "280px"
            }}
            valueOverrides={{
              defaultValue: milestoneOptions.find(m => m.id === thisAnimal?.feedcurve_milestones)
            }}
          />
          {/* <div>{animalInfo.milestone.dayNo}</div> */}
          <NewInput
            fieldOverrides={{
              fieldStyle: NewFieldStyles.greyTertiary,
              fieldType: NewFieldTypes.tertiaryField,
              fieldWidth: "100%",
            }}
            valueOverrides={{
              valueType: "number",
              defaultValue: animal.milestone_day ? Math.floor((Date.now() - animal.milestone_day) / (1000 * 60 * 60 * 24)) : undefined
            }}
            // onBlur={() => {
            //   dispatch({
            //     tempData: [{
            //       penId: penIndex,
            //       animalId: animalIndex,
            //       data: {
            //         milestone_day: ThisAnimal.milestone_day 
            //       }
            //     }]
            //   })
            // }}
            onChange={(e: string | number) => {
              dispatch({
                tempData: [{
                  penId: penIndex,
                  animalId: animalIndex,
                  data: {
                    milestone_day: Math.round((Date.now() - Number(e) * 86400000))
                  }
                }]
              })
            }}
          />
          <NewSelect
            onChange={(selectedValue) => {
              const milestones = feedCurveMilestones[Number(selectedValue?.id)];
              const now = Math.floor(Date.now());
              if (milestones === undefined) {
                dispatch({ tempData: [{ penId: penIndex, animalId: animalIndex, data: { feedcurve_id: Number(selectedValue?.id), fk_feedcurve_milestones: NaN, milestone_day: now } }] })
                return;
              }

              const milestone = milestones?.find((milestone) => milestone.milestoneId === animal.milestone?.milestone_id);
              if (milestone !== undefined) {
                dispatch({ tempData: [{ penId: penIndex, animalId: animalIndex, data: { feedcurve_id: Number(selectedValue?.id), fk_feedcurve_milestones: milestone.id } }] })
                return;
              }

              const newMilestone = milestones?.at(0);
              dispatch({ tempData: [{ penId: penIndex, animalId: animalIndex, data: { feedcurve_id: Number(selectedValue?.id), milestone_day: now, fk_feedcurve_milestones: newMilestone?.id } }] })

            }}
            options={feedCurveOptions ?? []}
            fieldOverrides={{
              fieldStyle: NewFieldStyles.greyTertiary,
              fieldType: NewFieldTypes.tertiaryField,
              fieldWidth: "100%",
              menuWidth: "280px"
            }}
            valueOverrides={{
              defaultValue: feedCurveOptions?.find(f => f.id === animal.feedcurve_id)
            }}
          />
          <FeedCorrectionPopover
            onChange={(selectedValue) => dispatch({ tempData: [{ penId: penIndex, animalId: animalIndex, data: { correction: selectedValue } }] })}
            defaultValue={thisAnimal?.correction}
            fieldOverrides={{
              fieldStyle: NewFieldStyles.greyTertiary,
              fieldType: NewFieldTypes.tertiaryField,
              fieldWidth: "100%"
            }}
          />
          <div>{ProgressWithoutExtra}</div>
          <div>{ProgressWithExtra}</div>
        </div>
      )
    case currentViewTypes.add:
      return (
        <div style={{ display: "grid", gridTemplateColumns: "45px 108px 45px 108px 45px 90px 50px", height: "35px", textAlign: "start", alignItems: "center", gap: "5px", cursor: "default", zIndex: "9999" }}>
          <div>{animal.animal_number}</div>
          <div>{animalInfo.milestone.name}</div>
          <div>{animalInfo.milestone.dayNo}</div>
          <div>{animalInfo.feedCurveName}</div>
          <div>{animalInfo.feedCorrection}</div>
          <div>{ProgressWithoutExtra}</div>
          <div>{ProgressWithExtra}</div>
        </div>
      )
    case currentViewTypes.move:
      return (
        <div style={{ display: "grid", gridTemplateColumns: "45px 20px 55px 108px 223px", height: "35px", textAlign: "start", alignItems: "center", gap: "5px", cursor: "default", zIndex: "9999" }}>
          <div>{animal.animal_number}</div>
          <glyphs.Arrow fillColor={colors.greyDarkPrimary} />
          <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}>{t("params.location", { defaultValue: "Location" })}</p>
          <NewSelect
            onChange={(selectedValue) => dispatch({ tempData: [{ penId: penIndex, animalId: animalIndex, data: { location: Number(selectedValue?.id) } }] })} // TODO
            options={locationOptions}
            fieldOverrides={{
              fieldStyle: NewFieldStyles.greyTertiary,
              fieldType: NewFieldTypes.tertiaryField,
              fieldWidth: "100%",
              menuWidth: "280px"
            }}
            valueOverrides={{
              defaultValue: locationOptions.find(l => l.id === animal.location_id)
            }}
          />
        </div>
      )
    default:
      return <>View STATE ERROR</>
  }
}

export default ANIMAL
